import React, { useState, useEffect, useRef } from "react";
import Select, { components } from "react-select";
import {
  AiOutlineQuestionCircle,
  AiOutlineThunderbolt,
  AiOutlineSync,
  AiOutlineAim,
  AiOutlineRise,
  AiOutlineSearch
} from "react-icons/ai";
import { FaSearch } from "react-icons/fa";
import { FiChevronDown, FiCheckCircle, FiSliders } from "react-icons/fi";
import { customSelectStyles } from "../utils/dashboardUtils";
import { RenderMarkdownWithLatex } from "../utils/utils";
// 1. IMPORT TIMERA (Dostosuj ścieżkę jeśli zapisałeś go gdzie indziej)
import TrialTimer from "../utils/TrialTimer"; 

// --- Komponenty Pomocnicze ---
const CustomMultiValueLabel = (props) => {
  const cleanLabel = props.data.label.split(" [ELO:")[0];
  return (
    <components.MultiValueLabel {...props}>
      {cleanLabel}
    </components.MultiValueLabel>
  );
};

const DifficultyTooltip = ({ tooltipRef }) => (
  <div
    ref={tooltipRef}
    className="absolute z-50 bottom-full -left-4 mb-2 w-[85vw] lg:w-72 rounded-lg bg-[#233855] p-4 text-sm text-white shadow-xl space-y-3 border border-white/10"
  >
    <h4 className="font-bold text-base text-white mb-1">Tryb Wyzwania (+300 ELO)</h4>
    <p className="leading-relaxed text-xs text-gray-200">
       Włącza zadania znacznie trudniejsze niż Twój obecny poziom.
    </p>
  </div>
);

// --- GŁÓWNY KOMPONENT FORMULARZA ---

const DashboardForm = ({
  mode,
  onSubmit,
  // Props Standard
  selectedFields,
  setSelectedFields,
  availableFields,
  levelModifier,
  setLevelModifier,
  // Props Custom
  handleCategoryClick,
  customSelectedFields,
  loadingSubfields,
  subfieldsData,
  selectedSubfields,
  handleSubfieldToggle,
  handleToggleAllSubfields,
  getSelectedSubfieldsForCategory,
  toggleSummaryExpansion,
  expandedSummaryIds,
  // Props Search
  onOpenSearch, 
  isSearchLoading, 
  onClearSelection, 
  selectedCount,
  // Widget Celu
  GoalWidget,
  // 2. NOWY PROP (Odbieramy datę z Dashboard.js)
  trialExpiryTimestamp 
}) => {

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFeaturesOpen, setIsFeaturesOpen] = useState(() => {
    const saved = localStorage.getItem('dashboard_features_open');
    return saved !== null ? saved === 'true' : true;
  });
  const [overflowVisible, setOverflowVisible] = useState(false);
  const [showAlgoDetails, setShowAlgoDetails] = useState(false);
  const [showDifficultyTooltip, setShowDifficultyTooltip] = useState(false);

  const difficultyTooltipRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('dashboard_features_open', String(isFeaturesOpen));
  }, [isFeaturesOpen]);

  useEffect(() => {
    let timer;
    if (isSettingsOpen) {
      timer = setTimeout(() => {
        setOverflowVisible(true);
      }, 500);
    } else {
      setOverflowVisible(false);
    }
    return () => clearTimeout(timer);
  }, [isSettingsOpen]);

  useEffect(() => {
     setShowAlgoDetails(false);
  }, [mode]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDifficultyTooltip && difficultyTooltipRef.current && !difficultyTooltipRef.current.contains(e.target) && !e.target.closest('button[aria-label="Informacje o poziomie trudności"]')) {
        setShowDifficultyTooltip(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDifficultyTooltip]);
  
  const getGroupedMultiSelectOptions = () => {
    const filtered = availableFields.filter(
      (field) => !selectedFields.some((s) => s.value === field.field_id)
    );
    const groups = {};
    filtered.forEach((field) => {
      const group = field.knowledge_level || "Nieokreślony";
      groups[group] = groups[group] || [];
      groups[group].push({
        label: `${field.field_name} [ELO: ${field.user_field_elo}]`,
        value: field.field_id,
        elo: field.user_field_elo,
      });
    });
    return Object.keys(groups)
      .sort()
      .map((g) => ({
        label: g,
        options: groups[g].sort((a, b) => a.label.localeCompare(b.label)),
      }));
  };

  // --- ANIMATED SOLVE BUTTON ---
const SolveButton = () => (
    <div className="w-full">
      <button
          type="submit"
          disabled={(mode === "standard" && selectedFields.length === 0) || (mode === "custom" && selectedSubfields.length === 0)}
          className="animate-pulse-gold w-full flex justify-center items-center gap-2 rounded-xl bg-[#233855] px-6 py-4 text-base font-apotek tracking-widest text-slate-100 hover:bg-[#2a4263] hover:text-white transition-all duration-200 cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed disabled:text-gray-200 disabled:animate-none disabled:shadow-none"
      >
          ROZWIĄZUJ!
      </button>

      {/* 3. WSTAWIENIE TIMERA TUTAJ - BĘDZIE WIDOCZNY W OBU TRYBACH */}
      <div className="px-1 lg:px-0">
          <TrialTimer expiryTimestamp={trialExpiryTimestamp} />
      </div>

    </div>
  );

  return (
    <div className="w-full mt-0">
      <form id="dashboard-form" className="space-y-5" onSubmit={onSubmit}>
        
        {/* === TRYB STANDARD === */}
        {mode === "standard" ? (
          <div className="flex flex-col gap-5">
            
            {/* 1. WIDGET CELU */}
            <div className="w-full">
                {GoalWidget}
            </div>

            {/* 2. BENEFIT ITEMS */}
            <div className="w-full px-1">
              <button
                type="button"
                onClick={() => setIsFeaturesOpen(v => !v)}
                className="flex items-center justify-between w-full mb-2 group"
              >
                <span className="text-xs font-bold uppercase tracking-widest text-gray-400 group-hover:text-gray-600 transition-colors">Dlaczego to działa?</span>
                <FiChevronDown className={`text-gray-300 group-hover:text-gray-500 transition-all duration-200 ${isFeaturesOpen ? 'rotate-180' : ''}`} size={14} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isFeaturesOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="space-y-2.5 pb-3">
                  <div className="flex items-center gap-2.5">
                    <AiOutlineRise className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
                    <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Dla Ciebie:</strong> Zadania dopasowane do Twojego poziomu (system ELO) i powtórki.</p>
                  </div>
                
                                    <div className="flex items-center gap-2.5">
                    <AiOutlineAim className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
                    <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Zadania typu maturalnego:</strong> Tylko to co będzie na maturze. </p>
                  </div>
                   {/* <div className="flex items-center gap-2.5">
                    <FiCheckCircle className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
                    <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Po prostu robisz zadania:</strong> Otwierasz. Rozwiązujesz. Tyle.</p>
                  </div> */}
                </div>
              </div>
            </div>

            {/* 3. PRZYCISK ROZWIĄZUJ */}
            <SolveButton />

            {/* 3. ZWINIĘTA KONFIGURACJA */}
            <div className="w-full">
                <button 
                    type="button"
                    onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                    className="flex items-center justify-between w-full bg-gray-50 px-3 py-2 rounded-lg text-xs font-semibold text-gray-500 hover:text-[#233855] hover:bg-gray-100 transition-all focus:outline-none group"
                >
                    <div className="flex items-center">
                        <FiSliders className="mr-2 text-lg" />
                        <span>Zaawansowana konfiguracja treningu</span>
                    </div>
                    <FiChevronDown className={`transition-transform duration-300 ${isSettingsOpen ? 'rotate-180' : ''}`} />
                </button>

                <div 
                    className={`transition-all duration-500 ease-in-out ${
                        isSettingsOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                    } ${
                        overflowVisible ? 'overflow-visible' : 'overflow-hidden'
                    }`}
                >
                    <div className="pt-4 pb-4 space-y-4 px-1">
                        <div className="relative">
                            <label htmlFor="categories" className="block text-xs text-gray-500 mb-1 ml-1 italic">
                                Wybierz działy:
                            </label>
                            
                            <Select
                                id="categories"
                                styles={{
                                    ...customSelectStyles,
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                                options={getGroupedMultiSelectOptions()}
                                value={selectedFields}
                                onChange={setSelectedFields}
                                isMulti
                                placeholder="Kliknij, aby wybrać..."
                                isSearchable={false}
                                closeMenuOnSelect={false}
                                blurInputOnSelect={false}
                                menuPlacement="top"
                                menuPortalTarget={document.body}
                                components={{ MultiValueLabel: CustomMultiValueLabel }}
                            />
                        </div>

                        <div className="flex items-center space-x-2 relative p-2 rounded-lg hover:bg-gray-50 transition-colors">
                            <input
                                id="hard_mode"
                                type="checkbox"
                                checked={levelModifier >= 300}
                                onChange={(e) => setLevelModifier(e.target.checked ? 300 : 0)}
                                className="h-5 w-5 cursor-pointer text-[#233855] border-gray-400 rounded focus:ring-[#345081]"
                            />
                            <label htmlFor="hard_mode" className="text-sm text-gray-800 cursor-pointer select-none font-medium">
                                Włącz Tryb Wyzwania
                            </label>
                            <button
                                type="button"
                                aria-label="Informacje o poziomie trudności"
                                onClick={() => setShowDifficultyTooltip((prev) => !prev)}
                                className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:text-gray-800"
                            >
                                <AiOutlineQuestionCircle className="h-4 w-4" />
                            </button>
                            {showDifficultyTooltip && <DifficultyTooltip tooltipRef={difficultyTooltipRef} />}
                        </div>
                    </div>
                </div>
            </div>
          </div>
        ) : (
          // === TRYB CUSTOM (BEZ ZMIAN) ===
          <div className="space-y-4">
             {/* Rozwijana informacja */}
             <div className="mb-4 px-1">
                <button
                    type="button"
                    onClick={() => setShowAlgoDetails(!showAlgoDetails)}
                    className="flex items-center justify-between w-full text-left group"
                >
                    <div className="flex items-center">
                        <FiCheckCircle className="text-[#007D00] mr-2 w-4 h-4" />
                        <span className="text-xs text-gray-600 font-medium group-hover:text-[#233855] transition-colors">
                            Sam wybierasz tematy, które chcesz przećwiczyć
                        </span>
                    </div>
                    <FiChevronDown 
                        className={`text-gray-400 w-4 h-4 transition-transform duration-300 ${showAlgoDetails ? 'rotate-180' : ''}`} 
                    />
                </button>

                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showAlgoDetails ? "max-h-40 opacity-100 mt-3" : "max-h-0 opacity-0"}`}>
                    <div className="flex flex-col gap-2 pl-2 border-l-2 border-gray-100 ml-2">
                            <div className="flex items-start text-xs text-gray-500">
                                 <AiOutlineSearch className="text-[#007D00] mr-2 text-base mt-0.5 flex-shrink-0" />
                                 <span>Dostęp do pełnej bazy zadań (bez limitów ELO) </span>
                            </div>
                            <div className="flex items-start text-xs text-gray-500">
                                <AiOutlineAim className="text-[#233855] mr-2 text-base mt-0.5 flex-shrink-0" />
                                <span>Wybierasz konkretne działy i tematy - uczysz się tego, czego akurat potrzebujesz</span>
                            </div>
                            <div className="flex items-start text-xs text-gray-500">
                                <AiOutlineThunderbolt className="text-[#E09F2E] mr-2 text-base mt-0.5 flex-shrink-0" />
                                 <span>Integracja z Inteligentnymi Arkuszami - przećwicz te typy zadań maturalnych, które sprawiły Ci problem</span>
                            </div>
                    </div>
                </div>
            </div>

            {/* Wyszukiwarka */}
            <div className="flex items-center gap-2">
                <button
    type="button"
    onClick={onOpenSearch}
    disabled={isSearchLoading}
    className="flex-1 group flex items-center justify-between bg-white border border-gray-300 hover:border-[#233855] hover:ring-2 hover:ring-[#233855]/10 rounded-xl px-4 py-3 transition-all shadow-sm disabled:opacity-70 disabled:cursor-wait"
>
    <div className="flex items-center text-gray-500 group-hover:text-[#233855]">
        {isSearchLoading ? (
            <>
                <AiOutlineSync className="mr-3 w-4 h-4 animate-spin text-[#233855]" />
                <span className="text-sm font-medium text-[#233855]">Pobieranie bazy zadań...</span>
            </>
        ) : (
            <>
                <FaSearch className="mr-3 w-4 h-4" />
                <span className="text-sm font-medium">Znajdź zadanie maturalne, temat...</span>
            </>
        )}
    </div>
</button>
            </div>

            {/* Licznik wybranych */}
            {selectedCount > 0 && (
                <div className="flex justify-end items-center mb-2 px-1 gap-2">
                    <span className="text-xs text-gray-400">Wybrano: <b className="text-gray-600">{selectedCount}</b></span>
                    <span className="text-gray-300 text-xs">|</span>
                    <button type="button" onClick={onClearSelection} className="text-xs font-bold text-[#8A0000] hover:underline uppercase">Wyczyść</button>
                </div>
            )}

            {/* Lista kategorii */}
            <div className="space-y-3">
                 {availableFields.map((field) => {
                      const selectedSubs = getSelectedSubfieldsForCategory(field.field_id);
                      const isExpanded = customSelectedFields.includes(field.field_id);
                      const isSummaryExpanded = expandedSummaryIds.includes(field.field_id);

                      return (
                        <div key={field.field_id}>
                          <button
                            type="button"
                            onClick={() => handleCategoryClick(field.field_id)}
                            className={`w-full flex justify-between items-center text-left p-3 rounded-lg border text-sm font-semibold transition-all ${
                              isExpanded
                                ? "bg-[#233855] text-white border-[#233855] shadow"
                                : "bg-gray-50 hover:bg-gray-100 border-gray-200"
                            }`}
                          >
                             <div className="w-full">
                               <div className="flex justify-between items-center w-full">
                                 <span>{field.field_name}</span>
                                 <span className={`transform transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}>▼</span>
                               </div>
                               {!isExpanded && selectedSubs.length > 0 && (
                                 <div className="mt-2 pt-2 border-t border-gray-300 text-left">
                                   <ul className="space-y-1">
                                     {selectedSubs.slice(0, isSummaryExpanded ? undefined : 3).map((sub) => (
                                       <li key={sub.subfield_id} className="text-xs font-normal text-gray-500 flex items-center">
                                         <span className="bg-green-500 w-1.5 h-1.5 rounded-full mr-2"></span>
                                         {sub.subfield_name}
                                       </li>
                                     ))}
                                     {selectedSubs.length > 3 && (
                                       <li>
                                         <button type="button" onClick={(e) => toggleSummaryExpansion(e, field.field_id)} className="text-xs font-medium text-[#233855] hover:underline pl-4">
                                           {isSummaryExpanded ? "... zwiń" : `... i ${selectedSubs.length - 3} więcej`}
                                         </button>
                                       </li>
                                     )}
                                   </ul>
                                 </div>
                               )}
                             </div>
                          </button>
                          
                          {isExpanded && (
                            <div className="p-3 mt-2 bg-white rounded-lg border border-gray-200 max-h-[60vh] overflow-y-auto">
                              {loadingSubfields === field.field_id && <p className="text-xs text-gray-500 text-center py-4">Ładowanie zagadnień...</p>}
                              {!loadingSubfields && subfieldsData[field.field_id] && (
                                  <>
                                    <div className="flex items-center justify-end space-x-4 px-2 pb-2 border-b border-gray-200 mb-2">
                                        <button type="button" onClick={() => handleToggleAllSubfields(field.field_id, true)} className="text-xs font-semibold text-blue-600 hover:text-blue-800">Zaznacz wszystkie</button>
                                        <button type="button" onClick={() => handleToggleAllSubfields(field.field_id, false)} className="text-xs font-semibold text-gray-500 hover:text-red-600">Odznacz wszystkie</button>
                                    </div>
                                    {subfieldsData[field.field_id].map(subfield => (
                                        <div key={subfield.subfield_id} className="py-3 border-b last:border-b-0 border-gray-200">
                                            <label className="flex items-start space-x-3 cursor-pointer group">
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedSubfields.includes(subfield.subfield_id)}
                                                    onChange={() => handleSubfieldToggle(subfield.subfield_id)}
                                                    className="h-4 w-4 mt-1 flex-shrink-0 rounded border-gray-300 text-[#233855] focus:ring-[#345081]"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex justify-between items-center gap-x-2">
                                                        <p className="flex-1 font-semibold text-sm text-gray-800 group-hover:text-[#233855]">{subfield.subfield_name}</p>
                                                    </div>
                                                    {subfield.example_problem && (
                                                        <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600">
                                                            <div className="p-2 bg-gray-50 rounded">
                                                                <RenderMarkdownWithLatex text={subfield.example_problem.question} />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                  </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                 })}
            </div>

            {/* Przycisk Rozwiązuj dla Custom */}
            <div className="pt-6">
                <SolveButton />
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default DashboardForm;